import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import { relativeTime } from "../../lib/time";
import { AttentionBadge } from "../sales/AttentionBadge";
import { NewCallDialog } from "../sales/NewCallDialog";
import { Button } from "../ui/Button";

/**
 * The sales calls on this customer, from inside the ticket.
 *
 * Two questions an agent has while reading a good email, neither of which the
 * helpdesk could answer: is somebody already ringing these people, and can I
 * get a call raised without leaving what I am reading. Both were a product
 * switch and a search by name away, which in practice meant the call was never
 * raised -- "I'll do it after this one" and then the next email arrives.
 *
 * Pending first and on its own, because that is the one that changes what the
 * agent does next. A customer with a call already open does not want a second
 * raised against them, and an agent who can see it can add what they have just
 * learnt to the one that exists instead.
 *
 * The history is deliberately not here. `SalesCallsPanel` on the CRM account is
 * the index of every call ever raised, and repeating it in a side panel four
 * sections tall would bury the one line that matters. What is kept is the most
 * recent closed call, as a date and an outcome -- enough to answer "have we
 * spoken to them lately" without becoming a list.
 */
export function TicketSalesCall({
  companyId,
  companyName,
  currentUserId,
  isAdmin,
}: {
  companyId: number;
  companyName: string;
  currentUserId: number;
  isAdmin: boolean;
}) {
  const [raising, setRaising] = useState(false);

  const calls = useQuery({
    queryKey: queryKeys.salesForCompany(companyId),
    queryFn: () => salesApi.forCompany(companyId),
    staleTime: 60_000,
  });

  /*
   * Only fetched once somebody opens the dialog. The groups and scores are a
   * request of their own, and a ticket pane that asked for them on every open
   * would be paying for a dialog nearly nobody opens on any given ticket.
   */
  const config = useQuery({
    queryKey: queryKeys.salesConfig,
    queryFn: salesApi.config,
    staleTime: 5 * 60_000,
    enabled: raising,
  });

  const open = (calls.data ?? []).filter((call) => call.status === "open");
  const lastClosed = (calls.data ?? []).find((call) => call.status === "closed");

  return (
    <>
      {open.length > 0 ? (
        <ul className="mb-2 space-y-1">
          {open.map((call) => (
            <li key={call.id}>
              <Link
                to={`/sales/calls/${call.id}`}
                className="block rounded-md border border-brand-500/50 bg-brand-50/50 px-2 py-1.5 hover:bg-brand-50 dark:bg-brand-950/20 dark:hover:bg-brand-950/40"
              >
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="min-w-0 truncate text-xs font-medium text-content">
                    {call.subject ?? "Sales call"}
                  </span>
                  <AttentionBadge attention={call.attention} size="xs" />
                </span>
                <span className="text-[11px] text-muted">
                  {call.assigneeName ? `with ${call.assigneeName}` : "in the pool"}
                  {" · "}
                  {call.attemptCount === 0
                    ? "no attempts yet"
                    : `${call.attemptCount} ${call.attemptCount === 1 ? "attempt" : "attempts"}`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-2 text-xs text-faint">
          {calls.isLoading
            ? "Checking…"
            : lastClosed
              ? /* Not "none": that reads as never spoken to, which is a
                   different thing from a pursuit that finished. */
                `No call pending. Last one closed ${
                  lastClosed.closedAt ? relativeTime(lastClosed.closedAt) : "earlier"
                }.`
              : "No call pending, and none raised before."}
        </p>
      )}

      <Button size="sm" variant="secondary" onClick={() => setRaising(true)}>
        {open.length > 0 ? "Raise another" : "Raise a call"}
      </Button>

      {raising ? (
        <NewCallDialog
          onClose={() => setRaising(false)}
          config={config.data}
          currentUserId={currentUserId}
          isAdmin={isAdmin}
          /*
           * Pre-linked, which is the whole point of raising it from here --
           * the account is already known, and typing its name again is how a
           * call ends up against the wrong one.
           *
           * No refresh callback: the dialog invalidates the whole `sales` key
           * on success, which this list is under, and then opens the call it
           * just made. Coming back to the ticket shows it as pending.
           */
          company={{ id: companyId, name: companyName }}
        />
      ) : null}
    </>
  );
}
